import { useCallback } from 'react';
import SearchInput from '../SearchInput.jsx';
import { topbarSearchIconTriggerClass } from './topbarClasses.js';
import { Search } from 'lucide-react';

function getSearchShortcutLabel() {
  if (typeof navigator === 'undefined') return 'Ctrl+K';
  const platform = navigator.userAgentData?.platform ?? navigator.platform ?? '';
  return /Mac|iPhone|iPad/i.test(platform) ? '⌘K' : 'Ctrl+K';
}

const SEARCH_SHORTCUT_LABEL = getSearchShortcutLabel();

/**
 * @param {'pill'|'icon'} [variant] — `pill` center bar (desktop); `icon` right toolbar (mobile, Figma).
 */
const TopbarSearch = ({
  variant = 'pill',
  searchProps = {},
  className = '',
  searchTooltip,
  onOpenSearch,
  showShortcutHint = true,
}) => {
  const hasSearchTooltip = typeof searchTooltip === 'string' && Boolean(searchTooltip.trim());
  const openHandler = onOpenSearch ?? searchProps.onOpen;
  const isTrigger = typeof openHandler === 'function';
  const searchAriaLabel = searchProps['aria-label'] ?? searchProps.placeholder ?? 'Open search';
  const shortcutAria = SEARCH_SHORTCUT_LABEL.includes('⌘') ? 'Meta+K' : 'Control+K';

  const handleTriggerClick = useCallback(
    (event) => {
      if (!isTrigger) return;
      event.preventDefault();
      event.stopPropagation();
      openHandler(event);
    },
    [isTrigger, openHandler],
  );

  const handleTriggerKeyDown = useCallback(
    (event) => {
      if (!isTrigger) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openHandler(event);
      }
    },
    [isTrigger, openHandler],
  );

  const handleIconClick = useCallback(
    (event) => (isTrigger ? handleTriggerClick(event) : searchProps.onClick?.(event)),
    [isTrigger, handleTriggerClick, searchProps.onClick],
  );

  const handleTriggerInputKeyDown = useCallback(
    (event) => {
      searchProps.onKeyDown?.(event);
      handleTriggerKeyDown(event);
    },
    [handleTriggerKeyDown, searchProps.onKeyDown],
  );

  if (variant === 'icon') {
    return (
      <button
        type="button"
        className={[topbarSearchIconTriggerClass, 'shrink-0 md:hidden', className]
          .filter(Boolean)
          .join(' ')}
        onClick={handleIconClick}
        aria-haspopup={isTrigger ? 'dialog' : undefined}
        aria-keyshortcuts={isTrigger ? shortcutAria : undefined}
        aria-label={searchAriaLabel}
      >
        <Search size={16} strokeWidth={2} aria-hidden />
      </button>
    );
  }

  const searchInputNode = (
    <SearchInput
      {...searchProps}
      readOnly={isTrigger ? true : searchProps.readOnly}
      onClick={isTrigger ? handleTriggerClick : searchProps.onClick}
      onKeyDown={isTrigger ? handleTriggerInputKeyDown : searchProps.onKeyDown}
      showTooltip={hasSearchTooltip && !isTrigger}
      tooltipText={searchTooltip}
      className={[
        'h-6 w-full',
        '[&_input]:h-6 [&_input]:rounded-lg [&_input]:py-0 [&_input]:pl-8',
        isTrigger && showShortcutHint ? '[&_input]:pr-14' : '[&_input]:pr-3',
        '[&_input]:border-edge [&_input]:bg-surface [&_input]:text-xs [&_input]:leading-4',
        'dark:[&_input]:bg-[rgba(255,255,255,0.1)]',
        '[&_input]:placeholder:text-content-faint [&_span]:left-2.5 [&_span]:text-content-muted [&_span_svg]:h-3.5 [&_span_svg]:w-3.5',
        isTrigger ? '[&_input]:cursor-pointer' : '',
        className,
        searchProps.className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );

  if (!isTrigger) {
    return <div className="w-[250px]">{searchInputNode}</div>;
  }

  return (
    <div
      className={['relative w-[250px] cursor-pointer', className].filter(Boolean).join(' ')}
      role="button"
      tabIndex={0}
      onClick={handleTriggerClick}
      onKeyDown={handleTriggerKeyDown}
      aria-haspopup="dialog"
      aria-keyshortcuts={shortcutAria}
      aria-label={searchAriaLabel}
    >
      {searchInputNode}
      {showShortcutHint ? (
        <span
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-edge bg-surface-muted px-1.5 py-0.5 text-[10px] font-medium leading-none text-content-muted dark:border-white/10 dark:bg-white/5 dark:text-gray-400"
          aria-hidden
        >
          {SEARCH_SHORTCUT_LABEL}
        </span>
      ) : null}
    </div>
  );
};

export default TopbarSearch;
